import { upsertWorkOrder } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import {
	ErrorModal,
	getMomentTz,
	AssetTreePicker,
	useToken,
	HelmetDexteel,
} from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	MenuItem,
	TextField,
	Tooltip,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid2 from '@mui/material/Grid2';
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import Alert from "@mui/lab/Alert";

import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useWorkOrderModalsContext } from "../context/work-order-modals.context";
import { INITIAL_VALUES_MODAL } from "../models/ModalInitialValue";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import { CraftPicker } from "./picker/craftPicker";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";
import { TableWorkOrdesResoures } from "./work-order-resources/table-work-ordes-resoures";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	data: ModalWorkOrder | null;
	initialNewMode?: boolean;
};

export function EditWorkOrderModal({
	show,
	onHide,
	data,
	initialNewMode = false,
}: Props) {
	const { getUserName } = useToken();

	const {
		state: {
			modalWorkOrderSelected: {
				Comments,
				TemplateTitle,
				WorkOrderOpenedBy,
				WorkOrderName,
				AssetName,
				AssetId,
				WorkOrderId,
			},
		},
		state,
		actions: {
			setComments,
			setWorkOrderNumberOfWorkers,
			setTemplateTitle,
			setWorkOrderPlannedHours,
			setWorkOrderOpenedBy,
			setOpenTimestamp,
			setWorkOrderName,
			setTitle,
			setAssetName,
			setWorkOrderId,
			setDownHours,
			setDownDayCode,
			setAttachmentsLinked,
			setCraftId,
			setAssetId,
			setWorkOrderStatusCode,
			setTypeCode,
			setWorkOrderAttachmentsList,
			setModalWorkOrderSelected,
		},
	} = useWorkOrderModalsContext();

	const [saveSuccess, setSaveSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");

	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<ModalWorkOrder>({ defaultValues: INITIAL_VALUES_MODAL });

	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [userInput, setUserInput] = useState<string>("");
	const [openWorkOrdersResources, setOpenWorkOrdersResources] = useState(false);
	const [newMode, setNewMode] = useState(initialNewMode);
	const [successMessage, setSuccessMessage] = useState("");
	const [assetChanged, setAssetChanged] = useState(false);

	const handleWorkOrderResourcesDialog = () => {
		setOpenWorkOrdersResources(true);
	};

	const changeSearchAsset = (value: number | null) => {
		if (value !== null) {
			setValue("AssetId", value);
			setAssetId(value);
			if (value === 0) {
				// AssetId 0 represents Plant and doesn't exist in catAssets
				setAssetName("Plant");
				setIsLoadingAssetPath(false);
			} else if (value> 0) {
				GetAssetFullPath(value.toString());
			}
			setAssetChanged(true);
			setWorkOrderAttachmentsList([]);
		}
	};

	const appendComment = (newComment: string) => {
		const now = moment(new Date()).format("MM/DD/YYYY HH:mm");
		let newValue = "";

		if (
			Comments === "" ||
			Comments === null ||
			Comments === "null" ||
			Comments === "undefined" ||
			Comments === undefined
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				Comments + "\n" + getUserName() + " - " + now + " - " + newComment;
		}

		if (newComment != "") {
			setUserInput("");
			setComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleTitleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.stopPropagation();
		}
	};

	const handleClose = (event: Event | React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setSaveSuccess(false);
	};

	const handleCancel = async () => {
		setUserInput("");
		reset();
		onHide(false);
	};

	const changeComments = (value: string) => {
		setComments(value);
	};

	const title = watch("Title");
	const changeTitle = () => {
		setTitle(title);
	};

	const downHours = watch("DownHours");
	const changeDownHours = () => {
		setDownHours(downHours);
	};

	const downDayCode = watch("DownDayCode");
	const changeDownDayCode = () => {
		setDownDayCode(downDayCode);
	};

	const workOrderNumberOfWorkers = watch("WorkOrderNumberOfWorkers");
	const changeWorkOrderNumberOfWorkers = () => {
		setWorkOrderNumberOfWorkers(workOrderNumberOfWorkers);
	};

	const workOrderPlannedHours = watch("WorkOrderPlannedHours");
	const changeWorkOrderPlannedHours = () => {
		setWorkOrderPlannedHours(workOrderPlannedHours);
	};

	const craftId = watch("CraftId");
	const changeCraftId = () => {
		setCraftId(craftId);
	};
	const assetId = watch("AssetId");
	const changeAssetId = () => {
		setAssetId(assetId);
	};


	const onSubmit = async () => {
		setIsSubmitLoading(true);
		const res = await upsertWorkOrder(
			state.modalWorkOrderSelected as WorkOrder,
		);
		if (res.ok) {
			setAssetChanged(false);
			setSaveSuccess(true);
			setIsSubmitLoading(false);
			setSuccessMessage(
				newMode
					? "Work order created successfully"
					: "Work order edited successfully",
			);
			if (newMode) {
				setNewMode(false);
			}
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
			onHide(false);
		}
	};

	useEffect(() => {
		if (show && initialNewMode && data) {
			const assetIdNumber = typeof data.AssetId === "string" ? parseInt(data.AssetId) : (data.AssetId ?? 0);

			// Initialize the entire context state with data
			setModalWorkOrderSelected(data);
			setAssetId(assetIdNumber);

			if (data?.AssetName && data.AssetName !== "") {
				setAssetName(data.AssetName);
				setIsLoadingAssetPath(false);
			} else if (assetIdNumber === 0) {
				setAssetName("Plant");
				setIsLoadingAssetPath(false);
			} else if (assetIdNumber > 0) {
				GetAssetFullPath(assetIdNumber.toString());
			} else {
				setAssetName("");
				setIsLoadingAssetPath(false);
			}

			reset(data);
			setNewMode(true);
		} else if (data != null && show && !initialNewMode) {
			const assetIdNumber = typeof data.AssetId === "string" ? parseInt(data.AssetId) : data.AssetId;
			setAssetId(assetIdNumber);
			setWorkOrderStatusCode(data?.WorkOrderStatusCode);
			setComments(data?.Comments);
			setTemplateTitle(data?.TemplateTitle);
			setWorkOrderPlannedHours(data?.WorkOrderPlannedHours);
			setWorkOrderNumberOfWorkers(data?.WorkOrderNumberOfWorkers);
			setWorkOrderOpenedBy(data?.WorkOrderOpenedBy);
			setOpenTimestamp(data?.OpenTimestamp);
			setWorkOrderName(data?.WorkOrderName);
			setWorkOrderId(data?.WorkOrderId);
			setTitle(data?.Title);
			setDownHours(data?.DownHours);
			setDownDayCode(data?.DownDayCode ?? null);
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			setAttachmentsLinked(data?.AttachmentsLinked);
			if (data?.AssetName) {
				setAssetName(data.AssetName);
			} else if (assetIdNumber === 0) {
				setAssetName("Plant");
				setIsLoadingAssetPath(false);
			} else if (assetIdNumber && assetIdNumber> 0) {
				GetAssetFullPath(assetIdNumber.toString());
			} else {
				setAssetName("");
				setIsLoadingAssetPath(false);
			}
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			reset(data);
			if (data?.AssetId !== undefined && data?.AssetId !== null) {
				const assetIdNumber = typeof data.AssetId === "string" ? parseInt(data.AssetId) : data.AssetId;
				setValue("AssetId", assetIdNumber);
			}
			setWorkOrderAttachmentsList([]);
			setUserInput("");
			setIsSubmitLoading(false);
			setNewMode(initialNewMode);
		}
	}, [data, show, initialNewMode]);

	useEffect(() => {
		changeTitle();
	}, [title]);
	useEffect(() => {
		changeDownHours();
	}, [downHours]);
	useEffect(() => {
		changeDownDayCode();
	}, [downDayCode]);
	useEffect(() => {
		changeCraftId();
	}, [craftId]);
	useEffect(() => {
		changeWorkOrderNumberOfWorkers();
	}, [workOrderNumberOfWorkers]);
	useEffect(() => {
		changeWorkOrderPlannedHours();
	}, [workOrderPlannedHours]);
	useEffect(() => {
		changeAssetId();
	}, [assetId]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${newMode ? "New Work Order" : "Edit Work Order"}`} />
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<DialogTitle
						sx={{
							padding: "15px 24px 10px 24px",
							width: "100%",
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<Typography sx={{ fontSize: "1.7em", fontWeight: "bold" }}>
								{WorkOrderName ? `WORK ORDER: ${WorkOrderName}` : "WORK ORDER"}
							</Typography>
							{downDayCode && (
								<Tooltip
									title={
										downDayCode === "N"
											? "Next down day work order"
											: "Down day work order"
									}
								>
									<ScheduleIcon
										color={downDayCode === "N" ? "error" : "secondary"}
									/>
								</Tooltip>
							)}
						</div>
						{TemplateTitle && <Typography>{data?.TemplateTitle}</Typography>}
					</DialogTitle>
					<DialogContent dividers>
						<Grid2 container spacing={2}>
							<Grid2 sx={{ padding: 0, marginTop: "-5px" }} size={{ xs: 12 }}>
								<Controller
									name="Title"
									control={control}
									rules={{
										validate: (value) => value !== "",
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Title"
											variant="outlined"
											fullWidth
											error={!!errors.Title}
											margin="dense"
											onKeyDown={handleTitleKeyDown}
											autoComplete="off"
											value={value}
											onChange={(e) => onChange(e)}
											sx={{
												"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
													{
														borderColor: "primary.main",
														borderWidth: 2,
													},
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2
								container
								spacing={2}
								size={{ xs: 12 }}
								sx={{ paddingBottom: 0.5 }}
							>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<AssetTreePicker
										label="Asset"
										value={AssetId || 0}
										onChange={changeSearchAsset}
									/>
								</Grid2>

								<Grid2 size={{ md: 3, xs: 6 }}>
									<TextField
										label="Opened by"
										variant="outlined"
										disabled
										value={WorkOrderOpenedBy}
										fullWidth
										margin="dense"
										autoComplete="off"
									/>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 6 }}>
									<TextField
										label="Opened"
										variant="outlined"
										disabled
										value={moment(data?.OpenTimestamp).format(
											"MM/DD/YYYY HH:mm A",
										)}
										fullWidth
										margin="dense"
										autoComplete="off"
									/>
								</Grid2>
							</Grid2>
							<Grid2
								container
								spacing={2}
								size={{ xs: 12 }}
								sx={{ marginTop: "-20px" }}
							>
								<Grid2 size={{ md: 3, xs: 6 }}>
									<Controller
										name="CraftId"
										control={control}
										render={({ field: { onChange, value } }) => (
											<CraftPicker
												value={value as number}
												onChange={onChange}
											/>
										)}
									/>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 2, xs: 6 }}>
									<Controller
										name="WorkOrderPlannedHours"
										control={control}
										rules={{
											pattern: {
												value: /^-?\d*(.\d+)?$/,
												message: "Enter a valid number",
											},
										}}
										render={({ field: { onChange, value } }) => (
											<TextField
												type="number"
												label="Planned Hours"
												variant="outlined"
												error={!!errors.WorkOrderPlannedHours}
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value ?? ""}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															borderColor: "primary.main",
															borderWidth: 2,
														},
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ md: 2, xs: 6 }}>
									<Controller
										name="WorkOrderNumberOfWorkers"
										control={control}
										rules={{
											pattern: {
												value: /^-?\d*(.\d+)?$/,
												message: "Enter a valid number",
											},
										}}
										render={({ field: { onChange, value } }) => (
											<TextField
												type="number"
												label="Workers"
												variant="outlined"
												error={!!errors.WorkOrderNumberOfWorkers}
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value ?? ""}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															borderColor: "primary.main",
															borderWidth: 2,
														},
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 2, xs: 6 }}>
									<Controller
										name="DownHours"
										control={control}
										render={({ field: { onChange, value } }) => (
											<TextField
												type="number"
												label="Down Hours"
												variant="outlined"
												error={!!errors.DownHours}
												fullWidth
												margin="dense"
												autoComplete="off"
												// New orders are pre-filled with a default down-hours value by the list
												// they are created from (1 for Down Time, 0 for Routine) so they land in
												// the right list. Down Time stays editable at creation; Routine is shown
												// read-only (hours aren't needed there). Always editable when editing.
												disabled={newMode && !!data?.DownHoursReadOnly}
												value={value ?? ""}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															borderColor: "primary.main",
															borderWidth: 2,
														},
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 6 }}>
									<Controller
										name="DownDayCode"
										control={control}
										render={({ field: { onChange, value } }) => (
											<TextField
												select
												label="Down day"
												variant="outlined"
												fullWidth
												margin="dense"
												InputLabelProps={{ shrink: true }}
												SelectProps={{ displayEmpty: true }}
												value={value ?? ""}
												onChange={(e) =>
													onChange(
														e.target.value === "" ? null : e.target.value,
													)
												}
												// On creation the down-day category is fixed by the list the order
												// was opened from; it can be changed afterwards by editing or dragging.
												// Editable for every work order regardless of type (PM or RM).
												disabled={newMode}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															borderColor: "primary.main",
															borderWidth: 2,
														},
												}}
											>
												<MenuItem value="">Not required</MenuItem>
												<MenuItem value="D">Any down day</MenuItem>
												<MenuItem value="N">Next down day</MenuItem>
											</TextField>
										)}
									/>
								</Grid2>
							</Grid2>
						</Grid2>
						<Grid2 container justifyContent="flex-end" size={{ xs: 12 }}>
							<Grid2
								sx={{
									marginBottom: 1.25,
									marginTop: 2.75,
									padding: 0,
								}}
								size={{ xs: 12 }}
							>
								<Card
									variant="outlined"
									sx={{
										backgroundColor: "#E9F2F8",
										"& .input-group-text": {
											display: "none",
										},
										"& .MuiDialogContent-dividers": {
											padding: 0,
										},
									}}
								>
									<CardContent>
										<Typography
											sx={{
												fontSize: 15,
												fontWeight: "bold",
												color: "#0000008A",
											}}
										>
											Comments:
										</Typography>
										<SimpleTextAreaControl
											value={Comments || ""}
											onChange={changeComments}
											disabled
											showColor={false}
											minRows={3}
											maxRows={3}
											styleText={{
												border: "none",
												backgroundColor: "#E9F2F8",
												fontSize: 16,
											}}
										/>
									</CardContent>
								</Card>
							</Grid2>
							<Grid2
								container
								spacing={2}
								alignItems="center"
								size={{ xs: 12 }}
							>
								<Grid2 size={{ md: 9, xs: 12 }}>
									<TextField
										variant="outlined"
										margin="dense"
										multiline
										maxRows={3}
										value={userInput}
										onChange={(e) => setUserInput(e.target.value)}
										label="Comment"
										fullWidth
										onKeyDown={onKeyDown}
										autoComplete="off"
										sx={{
											"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
												{
													borderColor: "primary.main",
												},
										}}
									/>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={addNewComment}
										sx={{ height: "40px", marginTop: 0.25 }}
									>
										Add comment
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</DialogContent>
					<DialogActions
						sx={{
							padding: "0px 24px",
							marginTop: "24px",
							paddingBottom: "16px",
						}}
					>
						<Grid2 container spacing={2}>
							<Grid2
								sx={{ display: "flex", alignItems: "center" }}
								title={
									assetChanged
										? "You need to save to be able to manage resources"
										: ""
								}
								size={{ md: 4, xs: 4 }}
							>
								{newMode === false && (
									<Button
										fullWidth
										variant="contained"
										color="primary"
										disabled={assetChanged}
										onClick={handleWorkOrderResourcesDialog}
									>
										MANAGE RESOURCES
									</Button>
								)}
							</Grid2>
							<Grid2
								container
								justifyContent="flex-end"
								spacing={2}
								sx={{ margin: 0 }}
								size={{ md: 8, xs: 8 }}
							>
								<Grid2 size={{ md: 6, xs: 6 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										CANCEL
									</Button>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 6 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										SAVE
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</DialogActions>
				</form>
			</Dialog>
			<Snackbar
				open={saveSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					{successMessage}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")} />
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
			<TableWorkOrdesResoures
				show={openWorkOrdersResources}
				onHide={() => setOpenWorkOrdersResources(false)}
				AssetId={AssetId ?? null}
				WorkOrderId={WorkOrderId ?? null}
				WorkOrderName={WorkOrderName ?? ""}
				Title={state.modalWorkOrderSelected.Title ?? ""}
				AssetName={AssetName ?? ""}
				TypeCode={state.modalWorkOrderSelected.TypeCode ?? ""}
				CraftId={state.modalWorkOrderSelected.CraftId ?? undefined}
				OpenTimestamp={state.modalWorkOrderSelected.OpenTimestamp ?? undefined}
				CloseTimestamp={state.modalWorkOrderSelected.CloseTimestamp ?? undefined}
				initialResource="ATTACHMENTS"
			/>
		</>
	);
}
